import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormGroup, FormControl } from '@angular/forms';
import { FormlyFieldConfig } from '@ngx-formly/core';
import { HttpClient } from '@angular/common/http';
import { catchError, of } from 'rxjs';
import { FormlyOptionsService, FormlyService } from '@nx-ahs/formly/shared';
import { FormlyBuilderApiService } from '../../../services/formly-builder-api.service';

@Component({
	selector: 'nx-ahs-field-properties-panel',
	templateUrl: './field-properties-panel.component.html',
	styleUrl: './field-properties-panel.component.scss',
	standalone: false
})
export class FieldPropertiesPanelComponent implements OnChanges {
	@Input() selectedField: FormlyFieldConfig | null = null;
	@Output() fieldUpdated = new EventEmitter<FormlyFieldConfig>();
	@Output() fieldDeleted = new EventEmitter<void>();

	propertiesForm: FormGroup;
	expandedSections = {
		basic: false,
		validation: false,
		styling: false,
		advanced: false,
		api: false,
		nestedScripts: false
	};

	// API Testing
	apiTesting = false;
	apiTestResult: any = null;
	apiTestError: string | null = null;
	showApiTestResults = false;

	// Nested Script Configuration
	availableScripts: any[] = [];
	selectedScripts: { scriptId: number; name?: string; description?: string }[] = [];
	scriptSearchTerm: string = '';
	loadingScripts = false;

	constructor(
		private http: HttpClient,
		private formlyOptionsService: FormlyOptionsService,
		private formlyService: FormlyService,
		private builderApiService: FormlyBuilderApiService
	) {
		this.initializeForm();
	}

	ngOnChanges(changes: SimpleChanges): void {
		if (changes['selectedField'] && this.selectedField) {
			this.populateForm();
			if (this.isNestedScriptField) {
				this.loadAvailableScripts();
			}
		}
	}

	get hasSelectedField(): boolean {
		return !!this.selectedField;
	}

	get fieldType(): string {
		if (!this.selectedField?.type) return '';
		const type = this.selectedField.type;
		const typeString = typeof type === 'string' ? type : '';
		const labelMap: Record<string, string> = {
			'mat-formly-input': 'Text Input',
			'mat-formly-textarea': 'Text Area',
			'mat-formly-select': 'Select',
			'mat-formly-check': 'Checkbox',
			'mat-formly-radio': 'Radio',
			'mat-formly-date': 'Date',
			'mat-formly-static': 'Static Text',
			'mat-formly-hidden': 'Hidden Field',
			'mat-formly-richtext': 'Rich Text',
			'mat-formly-chips': 'Chips',
			'mat-formly-chips-autocomplete': 'Chips Autocomplete',
			'mat-formly-address': 'Address',
			'mat-formly-file-upload': 'File Upload',
			'mat-formly-group': 'Group',
			'mat-formly-repeater': 'Repeater',
			'mat-formly-nested-script': 'Nested Script',
			'mat-formly-setter': 'Setter'
		};
		return labelMap[typeString] || typeString;
	}

	get showInputTypeField(): boolean {
		const type = typeof this.selectedField?.type === 'string' ? this.selectedField.type : '';
		return type === 'mat-formly-input';
	}

	get showOptionsField(): boolean {
		const type = typeof this.selectedField?.type === 'string' ? this.selectedField.type : '';
		return ['mat-formly-select', 'mat-formly-radio', 'mat-formly-check'].includes(type);
	}

	get hasApiConfig(): boolean {
		return !!this.selectedField?.props?.api?.url;
	}

	get hasDependencies(): boolean {
		return !!(this.selectedField?.expressions?.hide || this.selectedField?.hideExpression);
	}

	get referencedFields(): string[] {
		const hideExpr = this.propertiesForm.get('hideExpression')?.value || '';
		if (!hideExpr) return [];
		return this.extractFieldReferences(hideExpr);
	}

	get isNestedScriptField(): boolean {
		const type = typeof this.selectedField?.type === 'string' ? this.selectedField.type : '';
		return type === 'mat-formly-nested-script';
	}

	get filteredAvailableScripts(): any[] {
		if (!this.scriptSearchTerm) {
			return this.availableScripts.filter(
				(script) => !this.selectedScripts.some((s) => s.scriptId === script.scriptId)
			);
		}
		const searchLower = this.scriptSearchTerm.toLowerCase();
		return this.availableScripts.filter(
			(script) =>
				!this.selectedScripts.some((s) => s.scriptId === script.scriptId) &&
				(script.name?.toLowerCase().includes(searchLower) ||
					script.scriptId?.toString().includes(searchLower) ||
					script.description?.toLowerCase().includes(searchLower))
		);
	}

	private extractFieldReferences(expression: string): string[] {
		const references = new Set<string>();

		// Match model.fieldName patterns
		const modelMatches = expression.match(/model\.(\w+)/g);
		if (modelMatches) {
			modelMatches.forEach((match) => {
				const fieldName = match.replace('model.', '');
				references.add(fieldName);
			});
		}

		// Match formState references
		const formStateMatches = expression.match(/formState\.(\w+)/g);
		if (formStateMatches) {
			formStateMatches.forEach((match) => {
				const fieldName = match.replace('formState.', '');
				references.add(fieldName);
			});
		}

		return Array.from(references);
	}

	testApiEndpoint(): void {
		const apiUrl = this.propertiesForm.get('apiUrl')?.value;

		if (!apiUrl) {
			this.apiTestError = 'Please enter an API URL';
			this.showApiTestResults = true;
			return;
		}

		// Reset previous results
		this.apiTesting = true;
		this.apiTestResult = null;
		this.apiTestError = null;
		this.showApiTestResults = true;

		// Construct full URL using FormlyOptionsService API URL (same as form fields use)
		const fullUrl = apiUrl.startsWith('http')
			? apiUrl
			: `${this.formlyOptionsService.apiUrl}${apiUrl.startsWith('/') ? apiUrl.substring(1) : apiUrl}`;

		// Make HTTP request
		this.http
			.get(fullUrl)
			.pipe(
				catchError((error) => {
					this.apiTesting = false;
					this.apiTestError = `Error: ${error.status} - ${error.statusText || error.message}`;
					return of(null);
				})
			)
			.subscribe((response) => {
				this.apiTesting = false;

				if (response) {
					this.apiTestResult = response;

					// Validate response structure
					const labelField = this.propertiesForm.get('apiItemFieldLabel')?.value;
					const valueField = this.propertiesForm.get('apiItemFieldValue')?.value;

					if (Array.isArray(response) && response.length > 0) {
						const sampleItem = response[0];
						if (labelField && !Object.prototype.hasOwnProperty.call(sampleItem, labelField)) {
							this.apiTestError = `Warning: Label field "${labelField}" not found in response`;
						}
						if (valueField && !Object.prototype.hasOwnProperty.call(sampleItem, valueField)) {
							this.apiTestError = this.apiTestError
								? `${this.apiTestError}. Value field "${valueField}" not found`
								: `Warning: Value field "${valueField}" not found in response`;
						}
					}
				}
			});
	}

	closeApiTestResults(): void {
		this.showApiTestResults = false;
		this.apiTestResult = null;
		this.apiTestError = null;
	}

	get apiTestResultJson(): string {
		if (!this.apiTestResult) return '';
		return JSON.stringify(this.apiTestResult, null, 2);
	}

	get apiTestResultCount(): number {
		if (Array.isArray(this.apiTestResult)) {
			return this.apiTestResult.length;
		}
		return 0;
	}

	private initializeForm(): void {
		this.propertiesForm = new FormGroup({
			key: new FormControl(''),
			label: new FormControl(''),
			placeholder: new FormControl(''),
			description: new FormControl(''),
			hint: new FormControl(''),
			required: new FormControl(false),
			disabled: new FormControl(false),
			exclude: new FormControl(false),
			className: new FormControl(''),
			hideExpression: new FormControl(''),
			defaultValue: new FormControl(''),
			inputType: new FormControl('text'),
			// API Configuration
			apiUrl: new FormControl(''),
			apiItemFieldLabel: new FormControl(''),
			apiItemFieldValue: new FormControl(''),
			apiMethod: new FormControl(null),
			apiRefDataName: new FormControl(''),
			apiOverride: new FormControl(false)
		});
	}

	private populateForm(): void {
		if (!this.selectedField) return;

		const props = this.selectedField.props || {};
		const api = props.api || {};
		this.propertiesForm.patchValue(
			{
				key: this.selectedField.key || '',
				label: props.label || '',
				placeholder: props.placeholder || '',
				description: props.description || '',
				hint: props.hint || '',
				required: props.required || false,
				disabled: props.disabled || false,
				exclude: props.exclude || false,
				className: this.selectedField.className || '',
				hideExpression: this.selectedField.hideExpression || '',
				defaultValue: this.selectedField.defaultValue || '',
				inputType: props.inputType || 'text',
				// API Configuration
				apiUrl: api.url || '',
				apiItemFieldLabel: api.itemFieldLabel || '',
				apiItemFieldValue: api.itemFieldValue || '',
				apiMethod: api.method || null,
				apiRefDataName: api.refDataName || '',
				apiOverride: api.override || false
			},
			{ emitEvent: false }
		);

		// Load nested scripts if applicable
		if (this.isNestedScriptField && props.scripts) {
			this.selectedScripts = [...props.scripts];
		} else {
			this.selectedScripts = [];
		}
	}

	onApplyChanges(): void {
		if (!this.selectedField) return;

		const formValue = this.propertiesForm.value;
		const updatedField: FormlyFieldConfig = {
			...this.selectedField,
			key: formValue.key || this.selectedField.key,
			className: formValue.className || undefined,
			hideExpression: formValue.hideExpression || undefined,
			defaultValue: formValue.defaultValue || undefined,
			props: {
				...this.selectedField.props,
				label: formValue.label || null,
				placeholder: formValue.placeholder || null,
				description: formValue.description || null,
				hint: formValue.hint || null,
				required: formValue.required || null,
				disabled: formValue.disabled || null,
				exclude: formValue.exclude || null
			}
		};

		// Add inputType for input fields
		if (this.showInputTypeField) {
			updatedField.props = {
				...updatedField.props,
				inputType: formValue.inputType || 'text'
			};
		}

		// Handle API Configuration
		if (formValue.apiUrl) {
			updatedField.props = {
				...updatedField.props,
				api: {
					url: formValue.apiUrl,
					itemFieldLabel: formValue.apiItemFieldLabel || null,
					itemFieldValue: formValue.apiItemFieldValue || null,
					method: formValue.apiMethod || null,
					refDataName: formValue.apiRefDataName || null,
					override: formValue.apiOverride || false,
					options: null
				}
			};
		} else if (updatedField.props?.api) {
			// Remove API config if URL is cleared
			delete updatedField.props.api;
		}

		// Handle Nested Scripts Configuration
		if (this.isNestedScriptField) {
			updatedField.props = {
				...updatedField.props,
				scripts: this.selectedScripts.length > 0 ? [...this.selectedScripts] : []
			};
		}

		// Clean up null/undefined values
		Object.keys(updatedField.props).forEach((key) => {
			if (
				updatedField.props[key] === null ||
				updatedField.props[key] === undefined ||
				updatedField.props[key] === ''
			) {
				delete updatedField.props[key];
			}
		});

		this.fieldUpdated.emit(updatedField);
	}

	onDeleteField(): void {
		this.fieldDeleted.emit();
	}

	toggleSection(section: keyof typeof this.expandedSections): void {
		this.expandedSections[section] = !this.expandedSections[section];
	}

	// Nested Script Methods
	async loadAvailableScripts(): Promise<void> {
		if (this.availableScripts.length > 0) return; // Already loaded

		this.loadingScripts = true;
		try {
			this.availableScripts = await this.builderApiService.getAllScripts();
			// Filter out deleted scripts
			this.availableScripts = this.availableScripts.filter((script) => !script.isDeleted);
		} catch (error) {
			console.error('Error loading scripts:', error);
		} finally {
			this.loadingScripts = false;
		}
	}

	addScript(script: any): void {
		if (!this.selectedScripts.some((s) => s.scriptId === script.scriptId)) {
			this.selectedScripts.push({
				scriptId: script.scriptId,
				name: script.name || `Script ${script.scriptId}`,
				description: script.description || ''
			});
			this.scriptSearchTerm = ''; // Clear search after adding
		}
	}

	removeScript(index: number): void {
		this.selectedScripts.splice(index, 1);
	}

	moveScriptUp(index: number): void {
		if (index > 0) {
			const temp = this.selectedScripts[index];
			this.selectedScripts[index] = this.selectedScripts[index - 1];
			this.selectedScripts[index - 1] = temp;
		}
	}

	moveScriptDown(index: number): void {
		if (index < this.selectedScripts.length - 1) {
			const temp = this.selectedScripts[index];
			this.selectedScripts[index] = this.selectedScripts[index + 1];
			this.selectedScripts[index + 1] = temp;
		}
	}
}
